import {
  applyCanvasTimerCommand,
  createInitialCanvasTimerState,
  type CanvasOp,
  type ClientMessage,
  type ServerMessage,
} from "@refinement-canvas/domain";
import type { RealtimeClient } from "./realtime-client";

/** テストから確定結果を観測するための記録。実サーバ相当の commit / reject の到達を表す。 */
export interface MockRealtimeSettlement {
  readonly clientOpId: string;
  /** true なら op.commit、false なら op.reject を配信した。 */
  readonly accepted: boolean;
  /** 対象がオブジェクト作成 op の場合の種別（sticky / connector など）。 */
  readonly objectType?: string | undefined;
}

/** 記録を保持する window の拡張。認証なし / E2E でのみ生える。 */
interface MockRealtimeWindow {
  __mockRealtimeSettlements?: MockRealtimeSettlement[] | undefined;
}

/** 保持する記録の最大件数（無制限に増やさないための上限）。 */
const SETTLEMENT_HISTORY_LIMIT = 50;

/**
 * 確定結果を window へ記録する。
 *
 * E2E は「コネクタが楽観表示されたあと、サーバ確定を経ても消えないこと」を確認したいが、
 * 楽観適用の時点で既に矢印は見えているため、固定時間の待機では確定前の状態を成功と誤判定しうる。
 * 記録を見て待てば、実時間に依存せず確定の到達を待てる。
 * イベントではなく配列へ積むのは、購読の登録が操作より後になる取りこぼしを避けるため。
 */
function recordSettlement(settlement: MockRealtimeSettlement): void {
  if (typeof window === "undefined") return;
  const target = window as unknown as MockRealtimeWindow;
  const history = target.__mockRealtimeSettlements ?? [];
  history.push(settlement);
  if (history.length > SETTLEMENT_HISTORY_LIMIT) history.splice(0, history.length - SETTLEMENT_HISTORY_LIMIT);
  target.__mockRealtimeSettlements = history;
}

// 認証なしモード / E2E テスト用のモックリアルタイムクライアント。
// 自分が送った op を op.commit としてエコーバックする（他者は存在しない）。
//
// ただしコネクタ（接続矢印）の作成だけは、実サーバと同じ参照整合の契約を模す。
// - 端点 2 件が永続化済みでなければ `op.reject` を返す
// - ただし `endpointCreateDependencies`（同一操作で作る端点の依存ヒント）に挙がっている端点の
//   欠落だけは、永続化が終わるまで上限付きで待ってから判定する（実サーバのバックオフ再読に相当）
//
// ここを素通しにすると、依存ヒントを付け忘れて端点の確定前にコネクタを送る退行
// （実環境では矢印が保存されない）を、mock 認証で動く E2E が検知できない。
export function createMockRealtimeClient(): RealtimeClient {
  let serverSeq = 0;
  let timer = createInitialCanvasTimerState();
  const recentTimerCommandIds = new Set<string>();
  const handlers = new Set<(message: ServerMessage) => void>();
  // 永続化済みオブジェクトの id（実サーバの DynamoDB 上の実体に相当）。
  // 反映は op.commit を配信する時点で行い、「送信したがまだ ack を返していない op」は
  // 未永続化として扱う。実サーバでも各 op は別々の Lambda 実行として並行処理されるため、
  // 送信順は永続化順を保証しない（送った直後に存在するとみなすと、この差が消えてしまう）。
  const persistedObjectIds = new Set<string>();
  // このクライアントが作成 op を送ったオブジェクトの id。
  // 認証なしモードでも初期状態は REST bootstrap（実 API）から載るため、MCP / REST で作られた
  // オブジェクトはこのクライアントを通らない。実サーバの検証は「DynamoDB に実在するか」であって
  // 同一セッション由来かは問わないため、ここに無い id は既に永続化済みとして扱い、
  // 送信順の検査対象から外す（そうしないと、再読込後の既存図形どうしを繋げなくなる）。
  const sessionCreatedObjectIds = new Set<string>();
  // このクライアントが削除 op を送ったオブジェクトの id。
  // 由来を問わず「サーバに行が無い」ことが分かるため、bootstrap 由来の端点でも削除後は検査対象へ戻す。
  const sessionDeletedObjectIds = new Set<string>();

  // 依存ヒントの端点を待つ回数の上限（実サーバの 5 段階バックオフに相当）。
  const ENDPOINT_WAIT_ATTEMPTS = 5;

  const reject = (clientOpId: string, reason: string): void => {
    const message: ServerMessage = { type: "op.reject", clientOpId, reason };
    setTimeout(() => {
      handlers.forEach((h) => h(message));
      // 拒否するのはコネクタ作成だけ（applyConnectorCreate からのみ呼ばれる）。
      recordSettlement({ clientOpId, accepted: false, objectType: "connector" });
    }, 0);
  };

  const commit = (op: CanvasOp): void => {
    serverSeq += 1;
    const message: ServerMessage = { type: "op.commit", op, serverSeq };
    setTimeout(() => {
      if (op.type === "object.create" || op.type === "pen.stroke.commit") {
        persistedObjectIds.add(op.object.id);
      } else if (op.type === "object.delete") {
        persistedObjectIds.delete(op.objectId);
      }
      handlers.forEach((h) => h(message));
      recordSettlement({
        clientOpId: op.clientOpId,
        accepted: true,
        objectType: op.type === "object.create" ? op.object.type : undefined,
      });
    }, 0);
  };

  /** コネクタ作成を、端点の永続化状況に応じて確定・保留・拒否のいずれかへ振り分ける。 */
  const applyConnectorCreate = (op: CanvasOp, attempt: number): void => {
    if (op.type !== "object.create" || op.object.type !== "connector") return;
    const endpointIds = [op.object.from.objectId, op.object.to.objectId];
    // 検査できるのは、このクライアントが作成・削除を送った端点だけ。裏を返すと、モックが保証するのは
    // 「端点の作成 op をコネクタより先に送っている限り、その永続化を待つ」ところまでである。
    const missing = endpointIds.filter(
      (id) =>
        (sessionCreatedObjectIds.has(id) || sessionDeletedObjectIds.has(id)) &&
        !persistedObjectIds.has(id)
    );
    if (missing.length === 0) {
      commit(op);
      return;
    }
    const dependencies = new Set(op.endpointCreateDependencies ?? []);
    // 依存ヒントに無い欠落は、実サーバと同じく待たずに拒否する。
    if (!missing.every((id) => dependencies.has(id))) {
      reject(op.clientOpId, "コネクタの端点が見つかりません。");
      return;
    }
    if (attempt >= ENDPOINT_WAIT_ATTEMPTS) {
      reject(op.clientOpId, "コネクタの端点が見つかりません。");
      return;
    }
    // 端点の作成 op が確定するのを待ってから読み直す。
    setTimeout(() => applyConnectorCreate(op, attempt + 1), 0);
  };

  const sendOp = (op: CanvasOp): void => {
    if (op.type === "object.create" || op.type === "pen.stroke.commit") {
      sessionCreatedObjectIds.add(op.object.id);
    } else if (op.type === "object.delete") {
      sessionDeletedObjectIds.add(op.objectId);
    }
    if (op.type === "object.create" && op.object.type === "connector") {
      applyConnectorCreate(op, 0);
      return;
    }
    commit(op);
  };

  const sendMessage = (message: ClientMessage): void => {
    if (message.type === "op.apply") {
      sendOp(message.op);
      return;
    }
    if (message.type === "timer.command") {
      if (!recentTimerCommandIds.has(message.commandId)) {
        timer = applyCanvasTimerCommand(timer, message.command, Date.now());
        recentTimerCommandIds.add(message.commandId);
      }
      const sync: ServerMessage = {
        type: "timer.sync",
        timer,
        commandId: message.commandId,
        serverNowEpochMs: Date.now(),
      };
      setTimeout(() => handlers.forEach((handler) => handler(sync)), 0);
      return;
    }
    if (message.type === "timer.request") {
      const acknowledgedCommandId =
        message.pendingCommandId !== undefined &&
        recentTimerCommandIds.has(message.pendingCommandId)
          ? message.pendingCommandId
          : undefined;
      const sync: ServerMessage = {
        type: "timer.sync",
        timer,
        serverNowEpochMs: Date.now(),
        commandId: acknowledgedCommandId,
      };
      setTimeout(() => handlers.forEach((handler) => handler(sync)), 0);
    }
  };

  const onMessage = (handler: (message: ServerMessage) => void): (() => void) => {
    handlers.add(handler);
    return () => {
      handlers.delete(handler);
    };
  };

  return {
    connect: async () => {
      const snapshot: ServerMessage = {
        type: "bootstrap.snapshot",
        serverSeq,
        objects: [],
      };
      handlers.forEach((h) => h(snapshot));
    },
    // 実クライアントと同じく、切断では購読を解除しない（購読の後始末は onMessage が返す
    // unsubscribe の責務）。ここで handlers を全消しすると、React StrictMode の二重マウントで
    // 先行マウントの後片付けが遅れて走ったときに、後続マウントが張った購読まで消えてしまい、
    // 以後 op.commit も op.reject も一切届かない（開発 / E2E だけサーバ応答が無い状態になる）。
    disconnect: () => {},
    sendOp,
    sendMessage,
    onMessage,
    // mock は切断・再接続しないため、close / reopen 通知は発生しない（購読のみ受け付ける no-op）。
    onClose: () => () => {},
    onReopen: () => () => {},
  };
}
